var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React from 'react';
import * as Api from './network';
import DashboardCanvas from './DashboardCanvas';
import WidgetEditor from './WidgetEditor';
import GlobalFilterBar from './GlobalFilterBar';
import FilterConfigModal from './FilterConfigModal';
// ── Dashboard card for the list view ──
const DashboardCard = ({ d, onOpen, onDelete, canDelete }) => (_jsx("div", { className: "col-md-4 col-lg-3", children: _jsxs("div", { className: "card h-100", style: { cursor: 'pointer' }, onClick: () => onOpen(d.id), children: [_jsxs("div", { className: "card-body", children: [_jsx("h6", { className: "card-title", children: d.name }), d.description && _jsx("p", { className: "card-text text-muted small", children: d.description }), _jsxs("div", { className: "text-muted", style: { fontSize: '0.75rem' }, children: ["Updated ", new Date(d.updated).toLocaleDateString()] })] }), canDelete && (_jsx("div", { className: "card-footer bg-transparent border-top-0 pt-0", children: _jsxs("button", { className: "btn btn-sm btn-outline-danger", onClick: e => { e.stopPropagation(); onDelete(d.id); }, children: [_jsx("i", { className: "bi bi-trash me-1" }), "Delete"] }) }))] }) }));
// ── Main component ──
const AnalyticsDashboard = ({ tenantId, canCreate, canDelete }) => {
    const [view, setView] = React.useState({ kind: 'list' });
    const [dashboards, setDashboards] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    // Dashboard builder state
    const [dashboard, setDashboard] = React.useState(null);
    const [widgets, setWidgets] = React.useState([]);
    const [widgetData, setWidgetData] = React.useState({});
    const [layout, setLayout] = React.useState([]);
    const [tables, setTables] = React.useState([]);
    const [editingWidget, setEditingWidget] = React.useState(undefined);
    const [editingName, setEditingName] = React.useState(false);
    const [dashName, setDashName] = React.useState('');
    // Page state
    const [pages, setPages] = React.useState([]);
    const [activePage, setActivePage] = React.useState(null);
    const [renamingPageId, setRenamingPageId] = React.useState(null);
    const [renamingPageTitle, setRenamingPageTitle] = React.useState('');
    const [pageMenuOpen, setPageMenuOpen] = React.useState(null);
    // Filter state
    const [filterConfigs, setFilterConfigs] = React.useState([]);
    const [activeFilters, setActiveFilters] = React.useState([]);
    const [filterConfigModalOpen, setFilterConfigModalOpen] = React.useState(false);
    // Close page menu on outside click
    React.useEffect(() => {
        if (!pageMenuOpen)
            return;
        const handler = () => setPageMenuOpen(null);
        document.addEventListener('click', handler);
        return () => document.removeEventListener('click', handler);
    }, [pageMenuOpen]);
    // Load dashboards + table schema
    React.useEffect(() => {
        setLoading(true);
        Promise.all([
            Api.getDashboards(tenantId, { pageSize: 200 }),
            Api.getTableSchema(),
        ]).then(([dashes, tbls]) => {
            var _a;
            setDashboards((_a = dashes === null || dashes === void 0 ? void 0 : dashes.items) !== null && _a !== void 0 ? _a : []);
            setTables(tbls !== null && tbls !== void 0 ? tbls : []);
            setLoading(false);
        });
    }, [tenantId]);
    // Load a specific dashboard
    const loadDashboard = (id) => __awaiter(void 0, void 0, void 0, function* () {
        const result = yield Api.getDashboard(id);
        if (!result)
            return;
        setDashboard(result.dashboard);
        setWidgets(result.widgets);
        setDashName(result.dashboard.name);
        setPages((result.pages || []).slice().sort((a, b) => a.sortOrder - b.sortOrder));
        setActivePage(null);
        try {
            const parsed = JSON.parse(result.dashboard.layoutConfig);
            setLayout(Array.isArray(parsed) ? parsed : []);
        }
        catch (_a) {
            setLayout([]);
        }
        try {
            const parsed = JSON.parse(result.dashboard.globalFilters || '{}');
            setFilterConfigs(parsed.configs || []);
        }
        catch (_b) {
            setFilterConfigs([]);
        }
        setActiveFilters([]);
        executeAllWidgets(result.widgets, []);
    });
    const executeAllWidgets = (wdgts, filters) => {
        const filtersToUse = filters !== null && filters !== void 0 ? filters : activeFilters;
        const initial = {};
        for (const w of wdgts)
            initial[w.id] = { data: null, loading: true, error: null };
        setWidgetData(initial);
        for (const w of wdgts) {
            let widgetTable = '';
            try {
                const qc = JSON.parse(w.queryConfig);
                widgetTable = qc.tableName || '';
            }
            catch ( /* ignore */_a) { /* ignore */ }
            const applicable = filtersToUse.filter(f => f.tableName === widgetTable);
            Api.executeWidget(w.id, applicable.length > 0 ? applicable : undefined).then(data => {
                setWidgetData(prev => (Object.assign(Object.assign({}, prev), { [w.id]: { data: data || [], loading: false, error: null } })));
            }).catch(err => {
                setWidgetData(prev => (Object.assign(Object.assign({}, prev), { [w.id]: { data: null, loading: false, error: (err === null || err === void 0 ? void 0 : err.message) || 'Query failed' } })));
            });
        }
    };
    // Open dashboard view
    const openDashboard = (id) => {
        setView({ kind: 'dashboard', id });
        loadDashboard(id);
    };
    // Create new dashboard
    const handleCreate = () => __awaiter(void 0, void 0, void 0, function* () {
        const [ok, result] = yield Api.createDashboard(tenantId, { name: 'Untitled Dashboard', description: '' });
        if (ok) {
            const dash = result;
            setDashboards(prev => [dash, ...prev]);
            openDashboard(dash.id);
        }
    });
    // Delete dashboard
    const handleDelete = (id) => __awaiter(void 0, void 0, void 0, function* () {
        if (!confirm('Delete this dashboard and all its widgets?'))
            return;
        yield Api.deleteDashboard(id);
        setDashboards(prev => prev.filter(d => d.id !== id));
    });
    // Layout change
    const handleLayoutChange = (newLayout) => {
        setLayout(newLayout);
        if (dashboard) {
            Api.saveDashboardLayout(dashboard.id, JSON.stringify(newLayout));
        }
    };
    // Save widget (create or update)
    const handleSaveWidget = (body) => __awaiter(void 0, void 0, void 0, function* () {
        if (!dashboard)
            return;
        if (editingWidget) {
            yield Api.updateWidget(editingWidget.id, body);
        }
        else {
            const [ok, result] = yield Api.createWidget(dashboard.id, body);
            if (ok) {
                const w = result;
                setWidgets(prev => [...prev, w]);
                setWidgetData(prev => (Object.assign(Object.assign({}, prev), { [w.id]: { data: null, loading: true, error: null } })));
                Api.executeWidget(w.id).then(data => {
                    setWidgetData(prev => (Object.assign(Object.assign({}, prev), { [w.id]: { data: data || [], loading: false, error: null } })));
                }).catch(err => {
                    setWidgetData(prev => (Object.assign(Object.assign({}, prev), { [w.id]: { data: null, loading: false, error: (err === null || err === void 0 ? void 0 : err.message) || 'Query failed' } })));
                });
            }
        }
        setEditingWidget(undefined);
        if (editingWidget) {
            loadDashboard(dashboard.id);
        }
    });
    // Delete widget
    const handleDeleteWidget = (id) => __awaiter(void 0, void 0, void 0, function* () {
        if (!confirm('Delete this widget?'))
            return;
        yield Api.deleteWidget(id);
        setWidgets(prev => prev.filter(w => w.id !== id));
        setWidgetData(prev => {
            const next = Object.assign({}, prev);
            delete next[id];
            return next;
        });
    });
    // Dashboard name save
    const saveDashboardName = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!dashboard)
            return;
        setEditingName(false);
        if (dashName !== dashboard.name) {
            yield Api.updateDashboard(dashboard.id, {
                name: dashName,
                description: dashboard.description || '',
                layoutConfig: dashboard.layoutConfig,
                globalFilters: dashboard.globalFilters,
            });
            setDashboard(prev => prev ? Object.assign(Object.assign({}, prev), { name: dashName }) : prev);
            setDashboards(prev => prev.map(d => d.id === dashboard.id ? Object.assign(Object.assign({}, d), { name: dashName }) : d));
        }
    });
    // Page management
    const handleCreatePage = () => __awaiter(void 0, void 0, void 0, function* () {
        if (!dashboard)
            return;
        const title = prompt('Page title:', 'New Page');
        if (!title)
            return;
        const [ok, result] = yield Api.createPage(dashboard.id, title);
        if (ok) {
            const page = result;
            setPages(prev => [...prev, page].sort((a, b) => a.sortOrder - b.sortOrder));
            setActivePage(page.id);
        }
    });
    const handleRenamePage = (pageId) => __awaiter(void 0, void 0, void 0, function* () {
        if (!renamingPageTitle.trim()) {
            setRenamingPageId(null);
            return;
        }
        yield Api.updatePage(pageId, renamingPageTitle.trim());
        setPages(prev => prev.map(p => p.id === pageId ? Object.assign(Object.assign({}, p), { title: renamingPageTitle.trim() }) : p));
        setRenamingPageId(null);
    });
    const handleDeletePage = (pageId) => __awaiter(void 0, void 0, void 0, function* () {
        if (!confirm('Delete this page? Widgets on this page will be moved to Overview.'))
            return;
        yield Api.deletePage(pageId);
        const orphaned = widgets.filter(w => w.pageId === pageId);
        for (const w of orphaned) {
            yield Api.assignWidgetPage(w.id, null);
        }
        setWidgets(prev => prev.map(w => w.pageId === pageId ? Object.assign(Object.assign({}, w), { pageId: null }) : w));
        setPages(prev => prev.filter(p => p.id !== pageId));
        if (activePage === pageId)
            setActivePage(null);
    });
    const handleAssignWidgetPage = (widgetId, pageId) => __awaiter(void 0, void 0, void 0, function* () {
        yield Api.assignWidgetPage(widgetId, pageId);
        setWidgets(prev => prev.map(w => w.id === widgetId ? Object.assign(Object.assign({}, w), { pageId }) : w));
    });
    // Filter config management
    const handleSaveFilterConfigs = (configs) => __awaiter(void 0, void 0, void 0, function* () {
        if (!dashboard)
            return;
        setFilterConfigs(configs);
        setActiveFilters([]);
        const globalFilters = JSON.stringify({ configs });
        yield Api.saveDashboardFilters(dashboard.id, globalFilters);
        setDashboard(prev => prev ? Object.assign(Object.assign({}, prev), { globalFilters }) : prev);
    });
    const handleActiveFiltersChange = (filters) => {
        setActiveFilters(filters);
        executeAllWidgets(widgets, filters);
    };
    // Filtered widgets for current page
    const filteredWidgets = React.useMemo(() => activePage === null
        ? widgets.filter(w => w.pageId === null || w.pageId === '')
        : widgets.filter(w => w.pageId === activePage), [widgets, activePage]);
    const backToList = () => {
        setView({ kind: 'list' });
        Api.getDashboards(tenantId, { pageSize: 200 }).then(d => { var _a; return setDashboards((_a = d === null || d === void 0 ? void 0 : d.items) !== null && _a !== void 0 ? _a : []); });
    };
    // ── LIST VIEW ──
    if (view.kind === 'list') {
        return (_jsxs("div", { children: [_jsxs("div", { className: "d-flex justify-content-between align-items-center mb-4", children: [_jsxs("h1", { className: "h3 mb-0", children: [_jsx("i", { className: "bi bi-bar-chart-line me-2" }), "Analytics"] }), canCreate && (_jsxs("button", { className: "btn btn-primary", onClick: handleCreate, children: [_jsx("i", { className: "bi bi-plus-lg me-1" }), " New Dashboard"] }))] }), loading ? (_jsx("div", { className: "text-center py-5", children: _jsx("span", { className: "spinner-border" }) })) : dashboards.length === 0 ? (_jsxs("div", { className: "text-center text-muted py-5", children: [_jsx("i", { className: "bi bi-bar-chart-line", style: { fontSize: '3rem' } }), _jsx("p", { className: "mt-2", children: "No dashboards yet. Create one to start building analytics." })] })) : (_jsx("div", { className: "row g-3", children: dashboards.map(d => (_jsx(DashboardCard, { d: d, onOpen: openDashboard, onDelete: handleDelete, canDelete: canDelete }, d.id))) }))] }));
    }
    // ── DASHBOARD BUILDER VIEW ──
    return (_jsxs("div", { className: "d-flex flex-column flex-grow-1", style: { minHeight: 0 }, children: [_jsxs("div", { className: "d-flex justify-content-between align-items-center mb-3", children: [_jsxs("div", { className: "d-flex align-items-center gap-3", children: [_jsxs("button", { className: "btn btn-sm btn-outline-secondary", onClick: backToList, children: [_jsx("i", { className: "bi bi-arrow-left me-1" }), "Back"] }), editingName ? (_jsx("input", { type: "text", className: "form-control form-control-sm", style: { width: 300 }, value: dashName, onChange: e => setDashName(e.target.value), onBlur: saveDashboardName, onKeyDown: e => e.key === 'Enter' && saveDashboardName(), autoFocus: true })) : (_jsxs("h5", { className: "mb-0", style: { cursor: 'pointer' }, onClick: () => setEditingName(true), title: "Click to rename", children: [_jsx("i", { className: "bi bi-bar-chart-line me-2" }), (dashboard === null || dashboard === void 0 ? void 0 : dashboard.name) || 'Dashboard', _jsx("i", { className: "bi bi-pencil ms-2", style: { fontSize: '0.75rem', opacity: 0.5 } })] }))] }), _jsxs("div", { className: "d-flex gap-2", children: [canCreate && (_jsxs("button", { className: "btn btn-sm btn-primary", onClick: () => setEditingWidget(null), children: [_jsx("i", { className: "bi bi-plus-lg me-1" }), "Add Widget"] })), _jsx("button", { className: "btn btn-sm btn-outline-secondary", onClick: () => executeAllWidgets(filteredWidgets), title: "Refresh all widgets", "aria-label": "Refresh all widgets", children: _jsx("i", { className: "bi bi-arrow-clockwise" }) })] })] }), _jsxs("ul", { className: "nav nav-tabs mb-0", children: [_jsx("li", { className: "nav-item", children: _jsx("button", { className: `nav-link${activePage === null ? ' active' : ''}`, onClick: () => setActivePage(null), children: "Overview" }) }), pages.map(page => (_jsx("li", { className: "nav-item position-relative", children: renamingPageId === page.id ? (_jsx("div", { className: "d-flex align-items-center px-2 py-1", children: _jsx("input", { type: "text", className: "form-control form-control-sm", style: { width: 140 }, value: renamingPageTitle, onChange: e => setRenamingPageTitle(e.target.value), onBlur: () => handleRenamePage(page.id), onKeyDown: e => { if (e.key === 'Enter')
                                    handleRenamePage(page.id); if (e.key === 'Escape')
                                    setRenamingPageId(null); }, autoFocus: true }) })) : (_jsxs("div", { className: "d-flex align-items-center", children: [_jsx("button", { className: `nav-link${activePage === page.id ? ' active' : ''}`, onClick: () => setActivePage(page.id), children: page.title }), _jsxs("div", { className: "dropdown", style: { marginLeft: -4 }, children: [_jsx("button", { className: "btn btn-sm p-0 text-muted border-0", style: { lineHeight: 1, fontSize: '0.7rem', opacity: 0.5 }, onClick: e => { e.stopPropagation(); setPageMenuOpen(pageMenuOpen === page.id ? null : page.id); }, title: "Page options", "aria-label": "Page options", children: _jsx("i", { className: "bi bi-three-dots-vertical" }) }), pageMenuOpen === page.id && (_jsxs("ul", { className: "dropdown-menu show", style: { position: 'absolute', top: '100%', left: 0, zIndex: 1050 }, children: [_jsx("li", { children: _jsxs("button", { className: "dropdown-item small", onClick: () => { setRenamingPageId(page.id); setRenamingPageTitle(page.title); setPageMenuOpen(null); }, children: [_jsx("i", { className: "bi bi-pencil me-2" }), "Rename"] }) }), _jsx("li", { children: _jsxs("button", { className: "dropdown-item small text-danger", onClick: () => { setPageMenuOpen(null); handleDeletePage(page.id); }, children: [_jsx("i", { className: "bi bi-trash me-2" }), "Delete"] }) })] }))] })] })) }, page.id))), _jsx("li", { className: "nav-item", children: _jsx("button", { className: "nav-link text-muted", onClick: handleCreatePage, title: "Add page", "aria-label": "Add page", children: _jsx("i", { className: "bi bi-plus-lg" }) }) })] }), _jsxs("div", { className: "d-flex align-items-start gap-2 mb-3 mt-3", children: [_jsx("div", { className: "flex-grow-1", children: _jsx(GlobalFilterBar, { configs: filterConfigs, active: activeFilters, onChange: handleActiveFiltersChange }) }), _jsx("button", { className: "btn btn-sm btn-outline-secondary flex-shrink-0", onClick: () => setFilterConfigModalOpen(true), title: "Configure filters", "aria-label": "Configure filters", children: _jsx("i", { className: "bi bi-gear" }) })] }), _jsx("div", { className: "flex-grow-1", style: { minHeight: 0 }, children: _jsx(DashboardCanvas, { widgets: filteredWidgets, widgetData: widgetData, layout: layout, tables: tables, pages: pages, activePage: activePage, onLayoutChange: handleLayoutChange, onEditWidget: w => setEditingWidget(w), onDeleteWidget: handleDeleteWidget, onAssignWidgetPage: handleAssignWidgetPage }) }), _jsx(WidgetEditor, { isOpen: editingWidget !== undefined, onClose: () => setEditingWidget(undefined), onSave: handleSaveWidget, tables: tables, editWidget: editingWidget }), _jsx(FilterConfigModal, { isOpen: filterConfigModalOpen, onClose: () => setFilterConfigModalOpen(false), onSave: handleSaveFilterConfigs, tables: tables, existing: filterConfigs })] }));
};
export default AnalyticsDashboard;
